import React from "react";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  IconButton,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { OrangeTextButton } from "../../common/OrangeTextButton";
import {
  AddEdgeDeviceDialogStep,
  EdgeDeviceProvisionCommandOptions,
  EdgeDeviceProvisionTokenResponse,
} from "./edgeDeviceInterfaces";
import {
  downloadEdgeDeviceBootstrapZip,
  fetchStagingWireguardServers,
  parseProvisionTokenError,
  requestEdgeDeviceProvisionToken,
} from "./fetchEdgeDevices";
import { ProvisionTokenSuccessView } from "./EdgeDeviceProvisioningSection";
import {
  advanceRoundRobinWireguardServer,
  peekRoundRobinWireguardServer,
} from "./provisionCommands";

const DEFAULT_SSH_USER = "root";

interface Props {
  open: boolean;
  onClose: () => void;
}

export const AddEdgeDeviceDialog = ({ open, onClose }: Props) => {
  const [step, setStep] = React.useState<AddEdgeDeviceDialogStep>("form");
  const [serialNumber, setSerialNumber] = React.useState("");
  const [macAddress, setMacAddress] = React.useState("");
  const [routerWanIp, setRouterWanIp] = React.useState("");
  const [sshUser, setSshUser] = React.useState(DEFAULT_SSH_USER);
  const [cameraLocalSubnet, setCameraLocalSubnet] = React.useState("");
  const [wireguardServer, setWireguardServer] = React.useState("");
  const [wireguardPool, setWireguardPool] = React.useState<string[]>([]);
  const [wireguardPoolFailed, setWireguardPoolFailed] = React.useState(false);
  const [commandOptions, setCommandOptions] =
    React.useState<EdgeDeviceProvisionCommandOptions | null>(null);
  const [provisionResponse, setProvisionResponse] =
    React.useState<EdgeDeviceProvisionTokenResponse | null>(null);
  const [errorMessage, setErrorMessage] = React.useState("");
  const [isDownloadingBootstrap, setIsDownloadingBootstrap] = React.useState(false);
  const [bootstrapDownloadError, setBootstrapDownloadError] = React.useState("");

  React.useEffect(() => {
    if (!open) {
      return;
    }
    let cancelled = false;
    fetchStagingWireguardServers()
      .then((result) => {
        if (cancelled || !result) {
          return;
        }
        // A pinned server comes back with an empty pool: it is the only choice.
        const pool = result.pool.length
          ? result.pool
          : [result.wireguard_server].filter(Boolean);
        setWireguardPool(pool);
        setWireguardServer(
          (current) => current || peekRoundRobinWireguardServer(pool)
        );
      })
      .catch(() => {
        // Commands fall back to a <wireguard-server> placeholder.
        if (!cancelled) {
          setWireguardPoolFailed(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const resetStateAndClose = () => {
    setStep("form");
    setSerialNumber("");
    setMacAddress("");
    setRouterWanIp("");
    setSshUser(DEFAULT_SSH_USER);
    setCameraLocalSubnet("");
    // Preselect the next zone now (the index advanced on submit) so the select
    // never shows a zone it isn't holding while the pool is re-fetched.
    setWireguardServer(peekRoundRobinWireguardServer(wireguardPool));
    setCommandOptions(null);
    setProvisionResponse(null);
    setErrorMessage("");
    setIsDownloadingBootstrap(false);
    setBootstrapDownloadError("");
    onClose();
  };

  const handleClose = () => {
    if (step === "loading") {
      return;
    }
    resetStateAndClose();
  };

  const handleSubmit = async () => {
    setErrorMessage("");
    setStep("loading");

    try {
      const response = await requestEdgeDeviceProvisionToken({
        serial_number: serialNumber.trim(),
        mac_address: macAddress.trim(),
      });
      advanceRoundRobinWireguardServer(wireguardPool, wireguardServer);
      setCommandOptions({
        routerWanIp,
        sshUser,
        cameraLocalSubnet,
        wireguardServer,
      });
      setProvisionResponse(response);
      setStep("success");
    } catch (reason) {
      setErrorMessage(parseProvisionTokenError(reason));
      setStep("form");
    }
  };

  const handleDownloadBootstrapZip = async () => {
    setBootstrapDownloadError("");
    setIsDownloadingBootstrap(true);

    try {
      await downloadEdgeDeviceBootstrapZip();
    } catch {
      setBootstrapDownloadError("Failed to download bootstrap ZIP.");
    } finally {
      setIsDownloadingBootstrap(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="add-edge-device-title"
      sx={{ "& *": { fontFamily: "Mulish" } }}
    >
      <DialogTitle
        id="add-edge-device-title"
        sx={{ fontFamily: "Mulish", fontWeight: 700, color: "#001943", pr: 6 }}
      >
        Generate Provision Token
      </DialogTitle>
      <IconButton
        aria-label="close add edge device dialog"
        onClick={handleClose}
        disabled={step === "loading"}
        sx={{
          position: "absolute",
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <CloseIcon />
      </IconButton>

      {step === "loading" ? (
        <DialogContent dividers sx={{ px: 4, py: 6 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
            }}
          >
            <CircularProgress size={32} />
            <p className="font-mulish text-[16px] text-[#001943]">
              Getting Edge Device token...
            </p>
          </Box>
        </DialogContent>
      ) : step === "success" && provisionResponse ? (
        <>
          <DialogContent dividers sx={{ px: 4, py: 3 }}>
            <div className="flex flex-col gap-4">
              <ProvisionTokenSuccessView
                provisionResponse={provisionResponse}
                isDownloadingBootstrap={isDownloadingBootstrap}
                bootstrapDownloadError={bootstrapDownloadError}
                onDownloadBootstrapZip={handleDownloadBootstrapZip}
                commandOptions={commandOptions ?? undefined}
              />
            </div>
          </DialogContent>
          <DialogActions sx={{ p: 2, px: 4 }}>
            <OrangeTextButton onClick={resetStateAndClose} text="Finish" disabled={false} />
          </DialogActions>
        </>
      ) : (
        <>
          <DialogContent dividers sx={{ px: 4, py: 3 }}>
            <div className="flex flex-col gap-5">
              <TextField
                id="edge_device_serial_number"
                value={serialNumber}
                variant="standard"
                label="Serial number"
                required
                onChange={(event) => setSerialNumber(event.target.value)}
                sx={{ width: "100%", "& *": { fontFamily: "Mulish" } }}
              />
              <TextField
                id="edge_device_mac_address"
                value={macAddress}
                variant="standard"
                label="MAC address"
                onChange={(event) => setMacAddress(event.target.value)}
                sx={{ width: "100%", "& *": { fontFamily: "Mulish" } }}
              />
              <TextField
                id="edge_device_router_wan_ip"
                value={routerWanIp}
                variant="standard"
                label="Router current WAN IP (optional)"
                onChange={(event) => setRouterWanIp(event.target.value)}
                sx={{ width: "100%", "& *": { fontFamily: "Mulish" } }}
              />
              <TextField
                id="edge_device_ssh_user"
                value={sshUser}
                variant="standard"
                label="SSH user"
                onChange={(event) => setSshUser(event.target.value)}
                sx={{ width: "100%", "& *": { fontFamily: "Mulish" } }}
              />
              <TextField
                id="edge_device_camera_local_subnet"
                value={cameraLocalSubnet}
                variant="standard"
                label="Camera local subnet (optional)"
                placeholder="e.g. 192.168.0.0/24"
                helperText="Only needed if the cameras are not on the same LAN as the router."
                onChange={(event) => setCameraLocalSubnet(event.target.value)}
                sx={{ width: "100%", "& *": { fontFamily: "Mulish" } }}
              />
              <TextField
                id="edge_device_wireguard_server"
                select
                value={wireguardServer}
                variant="standard"
                label="WireGuard server"
                helperText="Rotates to the next zone for each new device. Change it to keep this site in the same zone as its existing tunnels."
                SelectProps={{ native: true }}
                onChange={(event) => {
                  const nextValue = event.target.value;
                  setWireguardServer(nextValue);
                }}
                sx={{ width: "100%", "& *": { fontFamily: "Mulish" } }}
              >
                {!wireguardServer && (
                  <option value="">
                    {wireguardPoolFailed ? "Unavailable" : "Loading zones…"}
                  </option>
                )}
                {wireguardPool.map((host) => (
                  <option key={host} value={host}>
                    {host}
                  </option>
                ))}
              </TextField>
              {errorMessage && (
                <FormHelperText className="font-mulish text-red-600" role="alert">
                  {errorMessage}
                </FormHelperText>
              )}
            </div>
          </DialogContent>
          <DialogActions sx={{ p: 2, px: 4 }}>
            <OrangeTextButton onClick={handleClose} text="Cancel" disabled={false} />
            <OrangeTextButton
              onClick={handleSubmit}
              text="Submit"
              disabled={!serialNumber.trim()}
            />
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};
